import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";
import { useTranslation } from "react-i18next";
import Axios from "axios";

import { Button, ButtonToolbar, Heading, Table, Text } from "rsuite";
import { SortType } from "rsuite-table";

import i18n from "utils/i18n";
import constants from "utils/constants";
import { getModalTableHeight } from "utils/measurement-helper";
import { formatDateTimeShort } from "utils/date-helper";
import { getSorted } from "utils/sort-halper";

import { Tenant } from "store/environments/environment-types";
import { StorageAccount } from "store/storage-accounts/storage-accounts-types";
import { OperationErrorLog } from "store/common/operation-types";

import { loadManagementPolicyRulesErrors } from "api/environments/storage-accounts-api";
import { getErrorMessage } from "api/defaults";

import { AppDispatch, RootState } from "store/store";
import { getActiveEnvironmentId } from "store/environments/environment-selectors";

import { setModalLoaderVisibility } from "store/common/modal-window/modal-window-slices";
import { setErrorResponseMessageForModal } from "store/common/initialization/initialization-actions";

import ModalContainer from "components/Common/Modal/ModalContainer";
import PopoverCell from "components/Common/PopoverCell/PopoverCell";

import "./ErrorLogs.less";

const useAppDispatch = () => useDispatch<AppDispatch>();

interface Props {
    title?: string;
    open: boolean;

    setOpen?: (open: boolean) => void;
}

const ErrorLogs: React.FC<Props> = ({
    title = i18n.t("storageAccounts.errorLogs"),
    open,
    setOpen,
}: Props) => {
    const cancelTokenSource = Axios.CancelToken.source();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const activeEnvironmentId = useSelector((state: RootState): string =>
        getActiveEnvironmentId(state)
    );

    const [errorLogs, setErrorLogs] = useImmer<
        ReadonlyArray<OperationErrorLog<Tenant<StorageAccount>>>
    >([]);

    const [sortColumn, setSortColumn] =
        useState<keyof OperationErrorLog<Tenant<StorageAccount>>>(
            "editionDate"
        );
    const [sortType, setSortType] = useState<SortType>("desc");

    const tableHeight = getModalTableHeight(errorLogs.length);
    // const failureColor = "#dc3545";
    const fetchData = () => {
        dispatch(setModalLoaderVisibility(true));
        dispatch(setErrorResponseMessageForModal(""));

        Promise.all([fetchManagementPolicyRulesErrors()])
            .then((value) => {
                if (value.some((v) => typeof v == "string")) {
                    const errors = new Set<string>(
                        value.filter((v) => typeof v == "string")
                    );

                    const message = [...errors].join(". ");
                    dispatch(setErrorResponseMessageForModal(message));
                }
            })
            .finally(() => {
                dispatch(setModalLoaderVisibility(false));
            });
    };

    const fetchManagementPolicyRulesErrors = async (): Promise<
        string | null
    > => {
        try {
            const [payload, status] = await loadManagementPolicyRulesErrors(
                activeEnvironmentId,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setErrorLogs(
                    getSorted(
                        [...payload],
                        sortColumn as keyof OperationErrorLog<
                            Tenant<StorageAccount>
                        >,
                        sortType
                    )
                );

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!Axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const handleSortColumn = (sortColumn: string, sortType: any) => {
        setSortColumn(
            sortColumn as keyof OperationErrorLog<Tenant<StorageAccount>>
        );
        setSortType(sortType);

        setErrorLogs((draft) =>
            getSorted(
                draft,
                sortColumn as keyof OperationErrorLog<Tenant<StorageAccount>>,
                sortType
            )
        );
    };

    const handleModalClose = () => {
        setSortColumn("editionDate");
        setSortType("desc");

        setOpen?.(false);
    };

    useEffect(() => {
        if (open && activeEnvironmentId) {
            fetchData();
        }
    }, [open, activeEnvironmentId]);

    const header = <Heading level={1}>{title}</Heading>;
    const footer = (
        <ButtonToolbar>
            <Button
                style={{ height: "2.5rem", width: "5rem" }}
                type="submit"
                appearance="ghost"
                onClick={handleModalClose}
            >
                {t("deliverables.packages.history.close")}
            </Button>
        </ButtonToolbar>
    );

    return (
        <ModalContainer
            className={"management-policy-rules-error-logs__modal"}
            open={open}
            header={header}
            footer={footer}
            onClose={handleModalClose}
        >
            <Table
                shouldUpdateScroll={false}
                virtualized={true}
                data={errorLogs}
                height={tableHeight}
                sortColumn={sortColumn}
                sortType={sortType}
                onSortColumn={handleSortColumn}
            >
                <Table.Column flexGrow={2} sortable={true}>
                    <Table.HeaderCell>
                        {t(`storageAccounts.errorLogsTable.storageAccount`)}
                    </Table.HeaderCell>
                    <Table.Cell dataKey="data.data.name">
                        {(
                            rowData: OperationErrorLog<Tenant<StorageAccount>>
                        ) => <Text> {rowData.data.data.name}</Text>}
                    </Table.Cell>
                </Table.Column>

                <Table.Column flexGrow={3} sortable={true}>
                    <Table.HeaderCell>
                        {t(`storageAccounts.errorLogsTable.modifiedBy`)}
                    </Table.HeaderCell>
                    <Table.Cell dataKey="editor">
                        {(
                            rowData: OperationErrorLog<Tenant<StorageAccount>>
                        ) => <Text>{rowData.editor}</Text>}
                    </Table.Cell>
                </Table.Column>

                <Table.Column flexGrow={2} sortable={true}>
                    <Table.HeaderCell>
                        {t(`storageAccounts.errorLogsTable.modifiedOn`)}
                    </Table.HeaderCell>
                    <Table.Cell dataKey="editionDate">
                        {(
                            rowData: OperationErrorLog<Tenant<StorageAccount>>
                        ) => (
                            <Text>
                                {formatDateTimeShort(
                                    new Date(rowData.editionDate)
                                )}
                            </Text>
                        )}
                    </Table.Cell>
                </Table.Column>

                <Table.Column flexGrow={3}>
                    <Table.HeaderCell>
                        {t(`storageAccounts.errorLogsTable.failureReason`)}
                    </Table.HeaderCell>
                    <Table.Cell dataKey="errorMessage">
                        {(
                            rowData: OperationErrorLog<Tenant<StorageAccount>>
                        ) => (
                            <PopoverCell
                                className="error-text"
                                dataKey="errorMessage"
                                rowData={rowData}
                            />
                        )}
                    </Table.Cell>
                </Table.Column>
            </Table>
        </ModalContainer>
    );
};

export default ErrorLogs;
